/**
 * DocxPageSelector
 *
 * Thin wrapper around PdfPageSelector that serves the LibreOffice-converted PDF
 * for DOCX documents. Once the converted PDF exists (document.has_pdf_preview),
 * behaviour is 100% identical to PdfPageSelector — same page thumbnails, same
 * single/grid views, same chunk-list slot below each page.
 *
 * If the PDF is not yet ready (conversion still running during phase 1), a
 * friendly waiting state is shown instead.
 */

import { FileText, Loader2 } from 'lucide-react'
import { useMemo } from 'react'
import { apiClient } from '@/services/api/client'
import type { LakeDocument } from '@/services/api/lake'
import { lakeApi } from '@/services/api/lake'
import { PdfPageSelector, type PdfViewMode } from './PdfPageSelector'

interface DocxPageSelectorProps {
  document: LakeDocument
  /** Total number of pages — provided by react-pdf via onLoadSuccess */
  pageCount: number
  /** Set of currently selected page numbers (1-indexed) */
  selectedPages: Set<number>
  /** Called when a page is toggled on/off */
  onTogglePage: (page: number) => void
  /** Controlled view mode */
  viewMode: PdfViewMode
  onViewModeChange: (mode: PdfViewMode) => void
  /** Controlled current page for single view */
  currentPage: number
  onCurrentPageChange: (page: number) => void
  /**
   * Optional content rendered inside the scrollable area below the current page
   * (single-view only). Use this to inject the chunk list.
   */
  children?: React.ReactNode
}

export function DocxPageSelector({
  document,
  pageCount,
  selectedPages,
  onTogglePage,
  viewMode,
  onViewModeChange,
  currentPage,
  onCurrentPageChange,
  children,
}: DocxPageSelectorProps) {
  // Build the authenticated PDF source for react-pdf — points at the
  // /pdf-preview endpoint which streams the converted PDF from S3.
  const pdfSource = useMemo(() => {
    const url = lakeApi.getDocxPdfPreviewUrl(document.id)
    const token = apiClient.getToken()
    return { url, httpHeaders: token ? { Authorization: `Bearer ${token}` } : {} }
  }, [document.id])

  // PDF not yet converted — show a waiting state
  if (!document.has_pdf_preview) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground p-8">
        <div className="flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          <FileText className="h-5 w-5" />
        </div>
        <p className="text-sm text-center">
          Page preview is being generated during document processing.
        </p>
        <p className="text-xs text-muted-foreground/60 text-center">
          This happens automatically — check back once phase 1 indexing completes.
        </p>
      </div>
    )
  }

  // PDF ready — delegate entirely to PdfPageSelector.
  // From this point on, DOCX pages are rendered identically to any PDF.
  return (
    <PdfPageSelector
      pdfSource={pdfSource}
      pageCount={pageCount}
      selectedPages={selectedPages}
      onTogglePage={onTogglePage}
      viewMode={viewMode}
      onViewModeChange={onViewModeChange}
      currentPage={currentPage}
      onCurrentPageChange={onCurrentPageChange}
    >
      {children}
    </PdfPageSelector>
  )
}
